/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: TemplatesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Templates module.
----------------------------------------------------------------*/

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutTemplate, Plus, Edit, Trash2, Copy } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useContextStore } from '../../store/context';

import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import * as api from './api';

export default function TemplatesPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();
    const [templates, setTemplates] = useState<api.Template[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Domain Contextual Filtering
    const effectiveDomainId = selectedDomain?.id;

    // Grid State
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: number | null }>({
        isOpen: false,
        id: null
    });
    const [isDeleting, setIsDeleting] = useState(false);

    const loadTemplates = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await api.getTemplates(effectiveDomainId);
            setTemplates(data);
        } catch (error) {
            console.error('Failed to load templates', error);
            showError(t('provisioningTemplates.loadError', 'Failed to load templates'));
        } finally {
            setIsLoading(false);
        }
    }, [effectiveDomainId, showError, t]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const handleDelete = async () => {
        if (!deleteModal.id) return;
        setIsDeleting(true);
        try {
            await api.deleteTemplate(deleteModal.id);
            showSuccess(t('provisioningTemplates.deleteSuccess', 'Template deleted successfully'));
            setDeleteModal({ isOpen: false, id: null });
            loadTemplates();
        } catch (error) {
            const backendError = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
            if (backendError && backendError.includes('Template is in use')) {
                showError(t('provisioningTemplates.deleteErrorInUse', backendError));
            } else {
                showError(t('provisioningTemplates.deleteError', 'Failed to delete template'));
            }
        } finally {
            setIsDeleting(false);
        }
    };

    // Process Data (Client-side)
    const processedData = useMemo(() => {
        let filtered = [...templates];

        // Search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                item.name.toLowerCase().includes(q) ||
                item.vendor?.name.toLowerCase().includes(q) ||
                item.model?.model.toLowerCase().includes(q)
            );
        }

        // Sort
        filtered.sort((a, b) => {
            let aValue: unknown = a[sortBy as keyof typeof a];
            let bValue: unknown = b[sortBy as keyof typeof b];

            if (sortBy === 'vendor.name') {
                aValue = a.vendor?.name || '';
                bValue = b.vendor?.name || '';
            } else if (sortBy === 'model.model') {
                aValue = a.model?.model || '';
                bValue = b.model?.model || '';
            } else if (sortBy === 'serverSettings.name') {
                aValue = a.serverSettings?.name || '';
                bValue = b.serverSettings?.name || '';
            }

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        // Pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

        return { data: paginatedData, totalItems, totalPages };
    }, [templates, searchQuery, sortBy, sortOrder, currentPage, itemsPerPage]);

    const handleColumnToggle = (key: string) => {
        setColumnVisibility(prev => ({
            ...prev,
            [key]: !(prev[key] ?? true)
        }));
    };

    const columnsDefinition = useMemo(() => [
        {
            key: 'name',
            label: t('common.name', 'Name'),
            visible: true,
            sortable: true
        },
        {
            key: 'vendor.name',
            label: t('provisioningTemplates.vendor', 'Vendor'),
            visible: true,
            sortable: true,
            render: (_: unknown, row: api.Template) => row.vendor?.name
        },
        {
            key: 'model.model',
            label: t('provisioningTemplates.model', 'Model'),
            visible: true,
            sortable: true,
            render: (_: unknown, row: api.Template) => row.model?.model
        },
        {
            key: 'serverSettings.name',
            label: t('provisioningTemplates.server', 'Server'),
            visible: true,
            sortable: true,
            render: (_: unknown, row: api.Template) => row.serverSettings?.name
        },
        {
            key: 'actions',
            label: t('common.actions', 'Actions'),
            visible: true,
            sortable: false
        }
    ], [t]);

    const columns = useMemo(() => columnsDefinition.map(col => ({
        ...col,
        visible: columnVisibility[col.key] ?? col.visible
    })), [columnsDefinition, columnVisibility]);

    const renderActions = (row: api.Template) => (
        <div className="flex items-center justify-end gap-2">
            <SimpleTooltip content={t('common.edit', 'Edit')} position="top">
                <button
                    onClick={() => navigate(`/settings/provisioning/provisioning-templates/${row.uuid ?? row.id}`)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors text-blue-600 dark:text-blue-400"
                >
                    <Edit className="h-4 w-4" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.duplicate', 'Duplicate')} position="top">
                <button
                    onClick={() => navigate(`/settings/provisioning/provisioning-templates/new?duplicate=${row.uuid ?? row.id}`)}
                    className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors text-emerald-600 dark:text-emerald-400"
                >
                    <Copy className="h-4 w-4" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete', 'Delete')} position="top">
                <button
                    onClick={() => setDeleteModal({ isOpen: true, id: row.id })}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors text-red-600 dark:text-red-400"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </SimpleTooltip>
        </div>
    );

    return (
        <div className="p-6 space-y-6">
            {/* Added spacer to account for Fixed ModuleFormHeader which uses absolute/fixed positioning */}
            <div className="h-2"></div>

            <DataGridSimple
                title={t('provisioningTemplates.title', 'Provisioning Templates')}
                titleIcon={<LayoutTemplate className="h-5 w-5 text-primary" />}
                data={processedData.data}
                columns={columns}
                loading={isLoading}
                renderActions={renderActions}
                onAdd={() => navigate('/settings/provisioning/provisioning-templates/new')}
                addButtonText={t('common.add', 'Add')}
                onColumnToggle={handleColumnToggle}

                searchQuery={searchQuery}
                onSearch={setSearchQuery}
                searchPlaceholder={t('common.search', 'Search...')}

                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(column) => {
                    if (sortBy === column) {
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                        setSortBy(column);
                        setSortOrder('asc');
                    }
                }}

                pagination={{
                    page: currentPage,
                    limit: itemsPerPage,
                    total: processedData.totalItems,
                    totalPages: processedData.totalPages
                }}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, id: null })}
                onConfirm={handleDelete}
                title={t('common.deleteItem', 'Delete Template')}
                message={t('common.confirmDelete', 'Are you sure you want to delete this item?')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
                isLoading={isDeleting}
            />
        </div>
    );
}
